import "./App.css";
import React, {lazy, Suspense} from "react";
import {Navigate, BrowserRouter, Outlet, Route, Routes, useLocation} from "react-router-dom";
import Homepage from "../Homepage/Homepage";
import {ThemeProvider} from "@mui/material";
import { theme } from "./../../theme";
import Header from "../Header/Header";
import ProtectedRoute from "./ProtectedRoute";
import Loader from "../Loader/Loader";
import GenericError from "../Utilities/GenericError";
import ChunkErrorBoundary, {clearChunkReloadMarker} from "../Utilities/ChunkErrorBoundary";
import {LANDING_ROUTES} from "../Utilities/landingRoutes";
import {LoadingProvider} from "../../context/LoadingContext";
import {CmsProvider} from "../../context/CmsContext";
import {AuthProvider} from "../../context/AuthContext";
import {AccountProvider} from "../../context/AccountContext";
import usePaymentProvider from "../../services/PaymentProvider.ts";
import useTheme from "../../Themes/ThemeProvider.ts";
import {ProductFlowProvider} from "../../context/ProductFlowContext";
import {useTranslation} from "react-i18next";

const Account = lazy(() => import("../Account/Account"));
const PickProgram = lazy(() => import("../PickProgram/PickProgram"));
const CongratsPage = lazy(() => import("../CongratsPage/CongratsPage"));
const CashCoupon = lazy(() => import("../CashCoupon/CashCoupon"));
const DeviceLink = lazy(() => import("../DeviceLink/DeviceLink"));
const AddCoupon = lazy(() => import("../AddCoupon/AddCoupon"));
const PasswordUpdate = lazy(() => import("../PasswordUpdate/PasswordUpdate"));
const Questionnaire = lazy(() => import("../Account/subPages/Questionnaire/Questionnaire"));
const Invoice = lazy(() => import("../Invoice/Invoice"));
const DeviceManage = lazy(() => import("../DeviceManage/DeviceManage"));
const PickAddon = lazy(() => import("../PickAddon/PickAddon"));

function ChunkErrorFallback() {
    const {t} = useTranslation();

    return (
        <div className="route-fallback">
            <GenericError
                setClose={() => {}}
                showIcon={false}
                mainText={t("chunkLoadErrorTitle")}
                buttonText={t("chunkLoadErrorCTA")}
                executeFunction={() => {
                    clearChunkReloadMarker();
                    window.location.reload();
                }}
            >
                {t("chunkLoadErrorBody")}
            </GenericError>
        </div>
    );
}

function LazyRouteBoundary() {
    const location = useLocation();

    return (
        <ChunkErrorBoundary resetKey={location.pathname} fallback={<ChunkErrorFallback />}>
            <Suspense fallback={<div className="route-fallback"><Loader /></div>}>
                <Outlet />
            </Suspense>
        </ChunkErrorBoundary>
    );
}

function App() {
  const paymentProvider = usePaymentProvider();

  const channelTheme = useTheme();
  return (
    <div className="App">
      <div className="app-wrapper">
        <ThemeProvider theme={theme}>
            <LoadingProvider>
                <CmsProvider>
                    <AuthProvider>
                        <AccountProvider>
                            <ProductFlowProvider>
                                <BrowserRouter>
                                    <Header />
                                    <Routes>
                                        <Route element={<LazyRouteBoundary />}>
                                            <Route exact path={LANDING_ROUTES.home} element={<Homepage />} />
                                            <Route exact path={LANDING_ROUTES.programs} element={<Homepage goToProducts={true} />}/>

                                            <Route path={"/AddCoupon"} element={<AddCoupon />} />

                                            <Route element={<ProtectedRoute />}>
                                                <Route path={"/Account"} element={<Account />} />
                                                <Route path={"/invoice"} element={<Invoice />} />
                                                <Route path={"/Unsubscribe"} element={paymentProvider.UnsubscribeComponent} />
                                                <Route path={"/Questionnaire"} element={<Questionnaire />} />
                                                <Route path={"/PickProgram"} element={<PickProgram />} />
                                                <Route path={"/PickAddon"} element={<PickAddon />} />
                                                <Route path={"/CongratsPage"} element={<CongratsPage />} />
                                                <Route path={paymentProvider.paymentForm} element={paymentProvider.PaymentProviderComponent} />
                                                <Route path={"/Link"} element={<DeviceLink />} />
                                                <Route path={"/devices"} element={<DeviceManage />} />

                                                <Route path={"/CashCoupon"} element={<CashCoupon />} />
                                                <Route path={"/PasswordUpdate"} element={<PasswordUpdate />}/>
                                            </Route>
                                        </Route>

                                        <Route path={"*"} element={<Navigate replace to={LANDING_ROUTES.home} />} />
                                    </Routes>
                                    <channelTheme.Footer/>
                                </BrowserRouter>
                            </ProductFlowProvider>
                        </AccountProvider>
                    </AuthProvider>
                </CmsProvider>
            </LoadingProvider>
        </ThemeProvider>
      </div>
    </div>
  );
}

export default App;
